<template>
	<div
		class="global-booker"
		:style="`--offset: ${navHeight}px; --scroll-offset: ${scrollOffset}px;`"
		:class="{ 'global-booker__nav': nav, 'global-booker__nav--sticky': stickyNav, 'global-booker__nav--open': nav && state.booker }">
		<AccordionList v-if="nav" v-model:state="state">
			<AccordionItem id="booker" :defaultOpened="false">
				<template #summary>
					<div class="global-booker__container global-booker__container--mobile">
						<v-btn flat class="global-booker__nav-all">
							<Transition name="fade" mode="out-in">
								<Skeletor v-if="!isHydrated" class="global-booker__button-skeletor" />
							</Transition>
							<div class="global-booker__nav-field">
								<div class="global-booker__nav-field-value">
									<span v-if="!bookerState.selectedLocations.start">{{ $t('components.booker.pickup_location') }}</span>
									<span v-else>
										<!-- <span v-if="bookerState.selectedLocations.start.city">{{ bookerState.selectedLocations.start.city }}, </span
										>{{ bookerState.selectedLocations.start.name }} -->
										{{ title(bookerState.selectedLocations.start) }}
									</span>
								</div>
								<div class="global-booker__nav-field-label">
									{{ format(bookerState.selectedDates.start, 'dd MMM, HH:mm', { locale: pl }) }} -
									{{ format(bookerState.selectedDates.end, 'dd MMM, HH:mm', { locale: pl }) }}
								</div>
							</div>
							<div class="global-booker__nav-field-icon">
								<GlobalIcon name="edit" :height="20"></GlobalIcon>
							</div>
						</v-btn>
					</div>
				</template>
				<template #icon></template>
				<GlobalBookerContainer v-if="state.booker" nav accordion :location="location" />
			</AccordionItem>
		</AccordionList>

		<GlobalBookerContainer v-if="!state.booker" :nav="nav" :location="location" />
	</div>
	<v-overlay v-model="overlay" attach persistent close-on-back scroll-strategy="none" @click="overlay = false" />
</template>

<script lang="ts" setup>
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
// @ts-ignore "no types"
import { Skeletor } from 'vue-skeletor'
import { AccordionList, AccordionItem } from 'vue3-rich-accordion'
import { ELocationPurpose } from '~/data/bookerData'

const { y } = useWindowScroll()

const scrollOffset = computed(() => {
	return y.value > (navHeight ?? 0) ? y.value - 186 : 0
})
const { getStartDate, getEndDate } = useBookerService()
const startDate = getStartDate()
const endDate = getEndDate()
const navHeight = inject<number>('navHeight')!
const bookerStore = useBookerStore()
const bookerState = ref<IBookerState>(bookerStore.getBooker())
const overlay = ref(true)
const bookingStore = useBookingStore()
const state = ref<Record<string, boolean>>({})
const route = useRoute()
const localePath = useLocalePath()
const isHydrated = ref(false)

if (process.client) {
	const localDates = bookingStore.getLocalBookerDates
	const localLocations = bookingStore.getLocalBookerLocations

	if (localDates?.start && localDates?.end) {
		const isDatesCorrect = bookingStore.isDatesCorrectForRenteon(localDates.start, localDates.end)

		if (isDatesCorrect) {
			bookingStore.setBookerDates({
				start: localDates.start === null ? startDate : localDates.start,
				end: localDates.end === null ? endDate : localDates.end,
			})
		} else {
			bookingStore.setBookerDates({
				start: getStartDate(),
				end: getEndDate(),
			})
		}
	}

	if (localLocations) {
		bookingStore.setBookerLocations(localLocations)
	}

	if (!route.name?.toString().includes('reservation')) {
		bookingStore.fetchBooking()
	}
}

onMounted(() => {
	isHydrated.value = true
})

watch(
	() => bookerState.value,
	(newValue) => {
		if (
			newValue?.selectedLocations?.start &&
			!(
				newValue.selectedLocations.start?.crmID ||
				newValue.selectedLocations.start?.isDelivery ||
				newValue.selectedLocations.start?.purpose === ELocationPurpose.MEET_AND_GREET
			)
		) {
			newValue.selectedLocations.start = null
		}

		if (
			newValue?.selectedLocations?.end &&
			!(
				newValue.selectedLocations.end?.crmID ||
				newValue.selectedLocations.end?.isDelivery ||
				newValue.selectedLocations.start?.purpose === ELocationPurpose.MEET_AND_GREET
			)
		) {
			newValue.selectedLocations.end = null
		}
		bookerStore.setBooker(newValue)
	},
	{
		deep: true,
		immediate: true,
	},
)

const stickyNav = computed(() => {
	return route.path === localePath('cars')
})

watch(
	() => bookerState.value,
	() => {
		bookingStore.setBookerDates(bookerState.value.selectedDates)
		if (!bookerState.value.otherEndLocation) {
			bookingStore.setBookerLocations({ ...bookerState.value.selectedLocations, end: null })
		} else {
			bookingStore.setBookerLocations(bookerState.value.selectedLocations)
		}
	},
	{ deep: true },
)

watch(
	() => state.value,
	() => {
		if (state.value.booker) {
			overlay.value = true
		} else {
			overlay.value = false
		}
	},
	{ immediate: true, deep: true },
)

watch(
	() => overlay.value,
	() => {
		if (!overlay.value) state.value.booker = false
	},
)

watch(
	() => bookingStore.getBookerDates,
	() => {
		if (!bookingStore.getBookerDates) return
		bookerState.value.selectedDates.start = bookingStore.getBookerDates.start === null ? startDate : bookingStore.getBookerDates.start
		bookerState.value.selectedDates.end = bookingStore.getBookerDates.end === null ? endDate : bookingStore.getBookerDates.end
	},
	{ immediate: true, deep: true },
)

watch(
	() => bookingStore.getBookerLocations,
	() => {
		if (!bookingStore.getBookerLocations) return
		bookerState.value.selectedLocations.start = bookingStore.getBookerLocations.start
		bookerState.value.selectedLocations.end = bookingStore.getBookerLocations.end
		if (bookingStore.getBookerLocations.end) {
			bookerState.value.otherEndLocation = true
		}
	},
	{ immediate: true, deep: true },
)

const { locale } = useI18n()
const i18nService = usei18nService()
// const locationsStore = useLocationsStore()

// onMounted(() => {
// 	locationsStore.fetchLocationCities()
// })

const title = (location: any) => {
	if (!location.city) return location.name
	else if (location.purpose === ELocationPurpose.MEET_AND_GREET) return `${i18nService.$tDynamic('data.cities', location.city)}, ${location.name}`
	else if ((location.city && location.icon === 'airplane') || (location.city && location.icon === 'train')) {
		return `${locale.value === 'pl' ? location.name : location.nameEn}`
	} else {
		return `${i18nService.$tDynamic('data.cities', location.city)}, ${location.address_street} ${location.address_house_number ?? ''}`
	}
}

defineProps<{
	nav?: boolean
	location?: boolean
}>()

provide('bookerState', bookerState)
provide('state', state)
</script>

<style lang="scss">
@import '@/assets/scss/utils/accordion.scss';
@import 'vue-skeletor/dist/vue-skeletor.css';
@import './Booker';
</style>
